import { useEffect, useRef, useState } from "react";
import ServiceIcon from "../../ui/ServiceIcon/ServiceIcon";
import { navLinks, site } from "../../../data/site";
import styles from "./Navbar.module.css";

function Chevron({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

function Arrow({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h12m-5-5 5 5-5 5" />
    </svg>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);
  const pathname = window.location.pathname;

  const close = () => {
    setOpen(false);
    setOpenDropdown(null);
  };
  const closeDrawer = () => {
    close();
    toggleRef.current?.focus();
  };
  const homeHref = (href) =>
    href.startsWith("#") ? `${pathname === "/" ? "" : "/"}${href}` : href;
  const isCurrent = (href) => pathname === href || pathname.startsWith(`${href}/`) || (href === "/our-clients" && pathname.startsWith("/showcase-"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    let wasMobile = window.innerWidth <= 1080;
    const onResize = () => {
      const isMobile = window.innerWidth <= 1080;
      if (isMobile === wasMobile) return;
      wasMobile = isMobile;
      const focusWasInNav = navRef.current?.contains(document.activeElement);
      setOpen(false);
      setOpenDropdown(null);
      if (focusWasInNav) {
        if (isMobile) toggleRef.current?.focus();
        else navRef.current?.querySelector('a[href]')?.focus();
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const previousOverflowY = root.style.overflowY;
    root.style.overflowY = "hidden";
    const focusTimer = setTimeout(() => closeRef.current?.focus(), 50);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setOpenDropdown(null);
        toggleRef.current?.focus();
      }
      if (event.key !== "Tab") return;
      const focusable = [...navRef.current.querySelectorAll("a[href], button")]
        .filter((element) => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(focusTimer);
      root.style.overflowY = previousOverflowY;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) setOpenDropdown(null);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      const trigger = navRef.current?.querySelector('[aria-expanded="true"]');
      setOpenDropdown(null);
      if (!open) trigger?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openDropdown, open]);

  return (
    <header className={`${styles.nav} ${scrolled ? styles.scrolled : ""} ${open ? styles.menuOpen : ""}`}>
      <div className={`container ${styles.inner}`}>
        <a href="/" className={styles.brand} onClick={close} aria-label={`${site.name} home`}>
          <img src="/visible-online.png" alt={site.name} width="162" height="54" decoding="async" />
        </a>

        <nav id="mobile-navigation" ref={navRef} className={`${styles.links} ${open ? styles.show : ""}`} aria-label="Primary">
          <div className={styles.drawerHeader}>
            <span>Explore Visible Online</span>
            <button ref={closeRef} type="button" className={styles.close} aria-label="Close menu" onClick={closeDrawer}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
            </button>
          </div>
          {navLinks.map((link) => {
            const active = isCurrent(link.href);
            if (!link.children?.length) {
              return (
                <a key={link.href} href={homeHref(link.href)} className={`${styles.navLink} ${active ? styles.active : ""}`} aria-current={active ? "page" : undefined} onClick={close}>
                  {link.label}
                </a>
              );
            }
            const isOpen = openDropdown === link.label;
            const menuId = `nav-menu-${link.label.toLowerCase()}`;
            return (
              <div key={link.href} className={`${styles.dropdown} ${isOpen ? styles.ddOpen : ""}`} onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setOpenDropdown(null);
              }}>
                <button type="button" className={`${styles.ddToggle} ${active ? styles.active : ""}`} aria-expanded={isOpen} aria-controls={menuId}
                  onClick={() => setOpenDropdown((current) => current === link.label ? null : link.label)}>
                  {link.label}<Chevron className={styles.ddChevron} />
                </button>
                <div id={menuId} className={styles.mega} data-section={link.href.replace("/", "")}>
                  <div className={styles.megaHeading}>
                    <span>{link.label === "Services" ? "Built for your next chapter" : "Tools that move business forward"}</span>
                    <span>{link.label}</span>
                  </div>
                  <div className={styles.megaGrid}>
                    {link.children.map((child) => (
                      <a key={child.href} href={homeHref(child.href)} className={styles.megaItem} onClick={close}>
                        {child.icon && <span className={styles.megaIcon}><ServiceIcon id={child.icon} /></span>}
                        <span className={styles.megaBody}>
                          <span className={styles.megaTitle}>{child.label}<Arrow className={styles.megaArrow} /></span>
                          {child.description && <span className={styles.megaDesc}>{child.description}</span>}
                        </span>
                      </a>
                    ))}
                  </div>
                  {link.footer && (
                    <div className={styles.megaFooter}>
                      <a href={homeHref(link.footer.href)} className={styles.megaAll} onClick={close}>{link.footer.label}<Arrow className={styles.megaArrow} /></a>
                      {link.footer.cta && <a href={homeHref(link.footer.cta.href)} className={styles.megaCta} onClick={close}>{link.footer.cta.label}<Arrow className={styles.megaArrow} /></a>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <a href="/contact" id="contact-cta" className={styles.contactCta} onClick={close}>Let's talk<span className={styles.ctaIcon}><Arrow className={styles.ctaArrow} /></span></a>
          <p className={styles.drawerNote}>Your next chapter starts with a conversation.</p>
        </nav>
        <button ref={toggleRef} type="button" className={styles.toggle} aria-label="Open menu" aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen((value) => !value)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h16M4 16h16" /></svg>
        </button>
      </div>
      <div className={`${styles.overlay} ${open ? styles.overlayShow : ""}`} onClick={closeDrawer} aria-hidden="true" />
    </header>
  );
}
